'use client';

import { c as _c } from "react/compiler-runtime";
import { BadgeCheck, Bell, ChevronsUpDown, CreditCard, LogOut, ShieldIcon, Sparkles, UserRoundXIcon } from 'lucide-react';
import { useState } from 'react';
import { signOut, stopImpersonatingAndRedirect } from '@/actions/auth';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from '@/components/ui/sidebar';
import { Skeleton } from '@/components/ui/skeleton';
import { authClient } from '@/lib/auth-client';
import { isAdminRole } from '@/lib/auth-roles';
import Link from 'next/link';
function UserSkeleton() {
  const $ = _c(2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Skeleton className="h-8 w-8 rounded-lg" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <>{t0}<div className="grid flex-1 text-left text-sm leading-tight gap-1"><Skeleton className="h-3 w-24 rounded-lg" /><Skeleton className="h-3 w-32 rounded-lg" /></div></>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function UserInfo(t0) {
  const $ = _c(30);
  const {
    session,
    organizations,
    isImpersonating
  } = t0;
  if (!session) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <UserSkeleton />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  let organization;
  let t1;
  if ($[1] !== organizations || $[2] !== session.session) {
    organization = organizations?.find(o => o.id === session.session.activeOrganizationId) || organizations?.at(0);
    t1 = organization?.metadata ? JSON.parse(organization.metadata) as {
      hidden?: boolean;
    } : null;
    $[1] = organizations;
    $[2] = session.session;
    $[3] = organization;
    $[4] = t1;
  } else {
    organization = $[3];
    t1 = $[4];
  }
  const organizationMetadata = t1;
  const isOrganizationHidden = organizationMetadata?.hidden;
  const user = session.user;
  let t2;
  if ($[5] !== user.name) {
    t2 = user.name.split(" ").map(_temp).join("").slice(0, 2);
    $[5] = user.name;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const initials = t2;
  const t3 = user.image ?? undefined;
  let t4;
  if ($[7] !== t3 || $[8] !== user.name) {
    t4 = <AvatarImage src={t3} alt={user.name} />;
    $[7] = t3;
    $[8] = user.name;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== initials) {
    t5 = <AvatarFallback className="rounded-lg">{initials}</AvatarFallback>;
    $[10] = initials;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t4 || $[13] !== t5) {
    t6 = <Avatar className="h-8 w-8 rounded-lg">{t4}{t5}</Avatar>;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== isImpersonating) {
    t7 = isImpersonating ? <span className="absolute -top-1 -right-1 inline-flex size-4 items-center justify-center rounded-full bg-orange-100 text-orange-500"><ShieldIcon className="size-2.5" /></span> : null;
    $[15] = isImpersonating;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t6 || $[18] !== t7) {
    t8 = <div className="relative">{t6}{t7}</div>;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== user.name) {
    t9 = <span className="truncate font-medium">{user.name}</span>;
    $[20] = user.name;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  const t10 = (isOrganizationHidden ? null : organization?.name) || user.email;
  let t11;
  if ($[22] !== t10) {
    t11 = <span className="truncate text-xs">{t10}</span>;
    $[22] = t10;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== t11 || $[25] !== t9) {
    t12 = <div className="grid flex-1 text-left text-sm leading-tight">{t9}{t11}</div>;
    $[24] = t11;
    $[25] = t9;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== t12 || $[28] !== t8) {
    t13 = <>{t8}{t12}</>;
    $[27] = t12;
    $[28] = t8;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  return t13;
}
function _temp(name) {
  return name.charAt(0);
}
export function NavUser() {
  const $ = _c(34);
  const {
    data: session
  } = authClient.useSession();
  const {
    data: organizations
  } = authClient.useListOrganizations();
  const {
    isMobile
  } = useSidebar();
  const [isStoppingImpersonation, setIsStoppingImpersonation] = useState(false);
  const isImpersonating = Boolean((session?.session as {
    impersonatedBy?: string;
  } | undefined)?.impersonatedBy);
  const role = (session?.user as {
    role?: string;
  } | undefined)?.role;
  let t0;
  if ($[0] !== role) {
    t0 = isAdminRole(role);
    $[0] = role;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const isAdmin = t0;
  const handleSignOut = _temp2;
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = async () => {
      setIsStoppingImpersonation(true);
      localStorage.removeItem("activeLocation");
      const response = await stopImpersonatingAndRedirect();
      setIsStoppingImpersonation(false);
      if (response && !response.ok) {
        return;
      }
    };
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const handleStopImpersonating = t1;
  let t2;
  if ($[3] !== isImpersonating || $[4] !== organizations || $[5] !== session) {
    t2 = <DropdownMenuTrigger asChild={true}><SidebarMenuButton size="lg" className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground">{session ? <><UserInfo session={session} organizations={organizations} isImpersonating={isImpersonating} /><ChevronsUpDown className="ml-auto size-4" /></> : <UserSkeleton />}</SidebarMenuButton></DropdownMenuTrigger>;
    $[3] = isImpersonating;
    $[4] = organizations;
    $[5] = session;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const t3 = isMobile ? "bottom" : "right";
  let t4;
  if ($[7] !== isImpersonating || $[8] !== organizations || $[9] !== session) {
    t4 = <DropdownMenuLabel className="p-0 font-normal"><div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">{session ? <UserInfo session={session} organizations={organizations} isImpersonating={isImpersonating} /> : <UserSkeleton />}</div></DropdownMenuLabel>;
    $[7] = isImpersonating;
    $[8] = organizations;
    $[9] = session;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <DropdownMenuSeparator />;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== isImpersonating || $[13] !== isStoppingImpersonation) {
    t6 = isImpersonating ? <><div className="space-y-1"><div className="px-0.5 pt-1"><div className="flex items-center gap-2 rounded-md border border-orange-200 bg-orange-50 px-2 py-1.5 text-xs font-medium text-orange-600"><ShieldIcon className="size-3.5" />Mode impersonation actif</div></div><DropdownMenuItem onClick={() => {
          handleStopImpersonating();
        }} className="rounded-md px-2 py-1.5 text-orange-500 focus:bg-orange-500/5 dark:focus:bg-orange-100 focus:text-orange-500 *:[svg]:!text-orange-500" disabled={isStoppingImpersonation}><UserRoundXIcon />{isStoppingImpersonation ? "Arr\xEAt de l'impersonation..." : "Quitter l'impersonation"}</DropdownMenuItem></div><DropdownMenuSeparator /></> : null;
    $[12] = isImpersonating;
    $[13] = isStoppingImpersonation;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  let t8;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <DropdownMenuGroup><DropdownMenuItem><Sparkles />Upgrade to Pro</DropdownMenuItem></DropdownMenuGroup>;
    t8 = <DropdownMenuSeparator />;
    $[15] = t7;
    $[16] = t8;
  } else {
    t7 = $[15];
    t8 = $[16];
  }
  let t9;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <DropdownMenuItem asChild={true}><Link href="/app/settings/account"><BadgeCheck />Account</Link></DropdownMenuItem>;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== isAdmin) {
    t10 = isAdmin ? <DropdownMenuItem asChild={true}><Link href="/admin/organisations"><ShieldIcon />Admin</Link></DropdownMenuItem> : null;
    $[18] = isAdmin;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <DropdownMenuItem asChild={true}><Link href="/app/settings/billing"><CreditCard />Billing</Link></DropdownMenuItem>;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <DropdownMenuItem><Bell />Notifications</DropdownMenuItem>;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== t10) {
    t13 = <DropdownMenuGroup>{t9}{t10}{t11}{t12}</DropdownMenuGroup>;
    $[22] = t10;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <DropdownMenuSeparator />;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <DropdownMenuItem onClick={() => {
      handleSignOut();
    }}><LogOut />Log out</DropdownMenuItem>;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] !== t13 || $[27] !== t3 || $[28] !== t4 || $[29] !== t6) {
    t16 = <DropdownMenuContent className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg" side={t3} align="end" sideOffset={4}>{t4}{t5}{t6}{t7}{t8}{t13}{t14}{t15}</DropdownMenuContent>;
    $[26] = t13;
    $[27] = t3;
    $[28] = t4;
    $[29] = t6;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  if ($[31] !== t16 || $[32] !== t2) {
    t17 = <SidebarMenu><SidebarMenuItem><DropdownMenu>{t2}{t16}</DropdownMenu></SidebarMenuItem></SidebarMenu>;
    $[31] = t16;
    $[32] = t2;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  return t17;
}
async function _temp2() {
  localStorage.removeItem("activeLocation");
  await signOut();
}